import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import UdhariActionModal from "./UdhariActionModal";
import {
  Users,
  Landmark,
  Search,
  Plus,
  FileText,
  CheckCircle2,
  CircleSlash2,
  Pencil,
  Trash2,
  RotateCcw,
} from "lucide-react";
import useUdhari from "../../../services/Order/useUdhari";
import { GlobalContext } from "../../../Context/GlobalContext";
import CreditBillModal from "./CreditBillModal";
import CustomerInvoiceImageModal from "./CustomerInvoiceImageModal";
import InvoiceTable from "./InvoiceTable";
import LoadingScreen from "../../../utils/LoadingScreen";
import { formatDateTime } from "../../../utils/dateFormatter";

const formatAmount = (val) => {
  return Number(val || 0).toFixed(2);
};

const extractBillAmount = (data) => {
  const amountKeys = [
    "billAmount",
    "bill_amount",
    "amount",
    "totalAmount",
    "total_amount",
    "grandTotal",
    "grand_total",
    "netAmount",
    "net_amount",
    "payableAmount",
    "payable_amount",
    "dueAmount",
    "due_amount",
    "total",
  ];

  const findAmount = (value) => {
    if (value === null || value === undefined) return null;

    // JSON string response handle karega
    if (typeof value === "string") {
      const trimmedValue = value.trim();

      if (
        (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
        (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"))
      ) {
        try {
          return findAmount(JSON.parse(trimmedValue));
        } catch {
          return null;
        }
      }

      return null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const result = findAmount(item);

        if (result !== null && Number(result) > 0) {
          return result;
        }
      }

      return null;
    }

    if (typeof value === "object") {
      // Pehle known amount fields check karo
      for (const key of amountKeys) {
        if (value[key] !== undefined && value[key] !== null) {
          const cleanedAmount = String(value[key])
            .replace(/₹/g, "")
            .replace(/,/g, "")
            .trim();

          const numericAmount = Number(cleanedAmount);

          if (!Number.isNaN(numericAmount) && numericAmount > 0) {
            return numericAmount;
          }
        }
      }

      // Nested objects/arrays recursively check karo
      for (const nestedValue of Object.values(value)) {
        const result = findAmount(nestedValue);

        if (result !== null && Number(result) > 0) {
          return result;
        }
      }
    }

    return null;
  };

  return findAmount(data);
};

const Udhari = () => {
  const { userID } = useContext(GlobalContext);

  const {
    getCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    generateInvoiceImage,
    generateBill,
    settleBill,
    saveInvoice,
    getOrderInvoices,
    deleteInvoice,
    updateInvoiceStatus,
  } = useUdhari();

  const [tab, setTab] = useState("UDHAARI");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const [billingName, setBillingName] = useState("");
  const [billingGst, setBillingGst] = useState("");
  const [billRemarks, setBillRemarks] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isOldBill, setIsOldBill] = useState("No");
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [billType, setBillType] = useState("auto");
  const [billAmount, setBillAmount] = useState("");
  const [billLoading, setBillLoading] = useState(false);
  const [billUrl, setBillUrl] = useState("");
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creditFilter, setCreditFilter] = useState("Y");

  const [form, setForm] = useState({
    customerName: "",
    customerMobile: "",
    creditLimit: "",
    creditAllowed: "N",
    loyaltyPoints: "",
    remarks: "",
  });

  // ================= FETCH =================
  const fetchCustomers = async () => {
    try {
      setLoading(true);

      const res = await getCustomers({ userid: userID });

      if (res?.data?.responseCode === "00") {
        setRows(res.data.response || []);
      } else {
        setRows([]);
        toast.error(res?.data?.responseMessage || "Failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching");
    } finally {
      setLoading(false);
    }
  };

  const onFetchBill = async () => {
    try {
      if (!billRow?.customer_mobile) {
        return toast.warning("Customer mobile number not found");
      }

      if (!billingName?.trim()) {
        return toast.warning("Billing name is required");
      }

      if (!fromDate || !toDate) {
        return toast.warning("Please select from date and to date");
      }

      if (new Date(fromDate) > new Date(toDate)) {
        return toast.warning("From date cannot be greater than to date");
      }

      if (billType === "custom" && (!billAmount || Number(billAmount) <= 0)) {
        return toast.warning("Please enter a valid bill amount");
      }

      setBillLoading(true);
      setBillUrl("");

      const payload = {
        userid: userID,
        mobileNumber: String(billRow.customer_mobile),
        billingName: billingName.trim(),
        billingGst: billingGst?.trim() || "",
        remarks: billRemarks?.trim() || "",
        fromDate,
        toDate,
        isOldBill,

        // Auto bill fetch ke time 0 hi jayega
        billAmount: billType === "custom" ? String(Number(billAmount)) : "0",
      };

      console.log("GENERATE BILL PAYLOAD:", payload);

      const res = await generateBill(payload);

      console.log("GENERATE BILL RESPONSE:", res);

      if (!res || res?.responseCode !== "00") {
        toast.error(res?.responseMessage || "Failed to generate bill");
        return;
      }

      const imageUrl = res?.image_url || "";

      if (!imageUrl) {
        toast.error("Invoice image URL not found");
        return;
      }

      const fetchedAmount =
        billType === "custom"
          ? String(billAmount)
          : String(
              res?.response?.item?.totalOrderPrice ??
                res?.response?.item?.balance ??
                res?.response?.item?.subtotal ??
                "0",
            );

      console.log("FETCHED TOTAL ORDER PRICE:", fetchedAmount);

      // Save Invoice ke liye actual amount store
      setBillAmount(fetchedAmount);

      // Same fetched bill image
      setBillUrl(imageUrl);

      toast.success("Bill generated successfully");
    } catch (err) {
      console.error("Generate Bill Error:", err);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Error generating bill",
      );
    } finally {
      setBillLoading(false);
    }
  };

  const onSaveInvoice = async () => {
    try {
      if (!billUrl) {
        return toast.warning("Please fetch bill first");
      }

      if (!billRow?.customer_mobile) {
        return toast.warning("Customer mobile number not found");
      }

      if (!billAmount || Number(billAmount) <= 0) {
        return toast.warning("Bill amount not found. Please fetch bill again.");
      }

      if (!fromDate || !toDate) {
        return toast.warning("Please select from date and to date");
      }

      setBillLoading(true);

      const payload = {
        userid: String(userID || ""),
        mobileNumber: String(billRow.customer_mobile || ""),
        billingName: String(billingName?.trim() || ""),
        billAmount: String(billAmount || "0"),
        invoiceLink: String(billUrl || ""),

        // API field isOld hai, state ka naam isOldBill hai
        isOld: String(isOldBill || "No"),

        fromDate: String(fromDate || ""),
        toDate: String(toDate || ""),
      };

      console.log("SAVE INVOICE PAYLOAD:", payload);

      const res = await saveInvoice(payload);

      console.log("SAVE INVOICE RESPONSE:", res);

      if (res?.responseCode === "00") {
        toast.success(res?.responseMessage || "Invoice saved successfully");

        setBillOpen(false);
        setBillUrl("");
        setBillAmount("");
        setBillRow(null);
        setBillingName("");
        setBillingGst("");
        setBillRemarks("");
        setBillType("auto");
        setIsOldBill("No");

        setTab("INVOICES");
      } else {
        toast.error(res?.responseMessage || "Invoice save failed");
      }
    } catch (err) {
      console.error("Save Invoice Error:", err);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Error saving invoice",
      );
    } finally {
      setBillLoading(false);
    }
  };

  const onSendToCustomer = async () => {
    try {
      if (!billUrl) {
        return toast.warning("Please fetch bill first");
      }

      const payload = {
        userid: userID,
        mobileNumber: billRow?.customer_mobile,
        isOldBill,
        fromDate,
        toDate,
        billAmount: String(Number(billAmount || 0)),
        invoiceLink: billUrl,
      };

      console.log("SETTLE BILL PAYLOAD:", payload);

      const res = await settleBill(payload);

      if (res?.responseCode === "00") {
        toast.success("Sent to customer successfully");
        setBillOpen(false);
        fetchCustomers();
      } else {
        toast.error(res?.responseMessage || "Send failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending bill");
    }
  };

  useEffect(() => {
    if (userID && tab === "UDHAARI") fetchCustomers();
  }, [userID, tab]);

  useEffect(() => {
    if (tab === "INVOICES" && userID) {
      fetchInvoices();
    }
  }, [tab, userID]);

  // ================= FILTER =================
  const filtered = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return rows.filter((r) => {
      const name = String(r.customer_name || "").toLowerCase();
      const mobile = String(r.customer_mobile || "");

      const matchesSearch =
        name.includes(searchValue) || mobile.includes(searchValue);

      const matchesCredit =
        creditFilter === "ALL" || r.credit_allowed === creditFilter;

      return matchesSearch && matchesCredit;
    });
  }, [rows, search, creditFilter]);

  const handleToggleCredit = async (row) => {
    const newValue = row.credit_allowed === "Y" ? "N" : "Y";

    try {
      await updateCustomer({
        customerId: row.pk_customer_id,
        creditLimit: row.creditLimit,
        creditAllowed: newValue,
      });

      toast.success(`Credit ${newValue === "Y" ? "Enabled" : "Disabled"}`);

      fetchCustomers(); // refresh
    } catch {
      toast.error("Toggle failed");
    }
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    try {
      if (editing) {
        await updateCustomer({
          customerId: editing.pk_customer_id,
          creditLimit: String(parseFloat(form.creditLimit || 0)),
          creditAllowed: form.creditAllowed,
          loyaltyPoints: String(form.loyaltyPoints || 0),
          remarks: form.remarks || "",
        });

        toast.success("Updated");
      } else {
        if (!form.customerName || !form.customerMobile) {
          return toast.warning("Fill required fields");
        }

        await addCustomer({
          userid: userID,
          customerName: form.customerName,
          customerMobile: form.customerMobile,
          creditLimit: String(parseFloat(form.creditLimit || 0)),
          creditAllowed: form.creditAllowed,
          loyaltyPoints: String(form.loyaltyPoints || 0),
          remarks: form.remarks || "",
        });

        toast.success("Added");
      }

      setDrawerOpen(false);
      fetchCustomers();
    } catch {
      toast.error("Failed");
    }
  };

  // ================= DELETE =================
  const confirmDelete = async () => {
    try {
      await deleteCustomer({ customerId: deleteId });

      toast.success("Deleted successfully");

      setDeleteOpen(false);
      setDeleteId(null);

      fetchCustomers(); // refresh
    } catch {
      toast.error("Delete failed");
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoiceLoading(true);

      const res = await getOrderInvoices({ userid: userID });

      if (res?.responseCode === "00") {
        setInvoiceRows(res.response || []);
      } else {
        setInvoiceRows([]);
        toast.error(res?.responseMessage || "Failed");
      }
    } catch (err) {
      toast.error("Error fetching invoices");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      const res = await deleteInvoice({ invoiceId: id });

      if (res?.responseCode === "00") {
        toast.success("Invoice deleted");
        fetchInvoices();
      } else {
        toast.error(res?.responseMessage || "Delete failed");
      }
    } catch {
      toast.error("Error deleting invoice");
    }
  };

  const handlePaymentSubmit = async (row, data) => {
    try {
      const payload = {
        invoiceId: row.pk_invoice_id,
        paymentMode: data.paymentMode,
        paymentStatus: data.paymentStatus,
      };

      const res = await updateInvoiceStatus(payload);

      if (res?.responseCode === "00") {
        toast.success("Payment updated");
        fetchInvoices();
      } else {
        toast.error(res?.responseMessage || "Failed");
      }
    } catch {
      toast.error("Error updating payment");
    }
  };

  return (
    <>
   
      <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <div className="mb-5 bg-white p-5 rounded-lg montserrat-font-1">
          <h1 className="text-2xl font-semibold text-gray-800">
            Credit & Invoices
          </h1>
          <p className="text-sm text-gray-500 montserrat-font-2">
            Manage customer udhaari and invoices in one place.
          </p>
        </div>

        {/* TABS */}
        <div className="border-b mb-6 bg-white p-4 rounded-lg montserrat-font-1">
          <div className="flex gap-6 text-sm font-medium">
            <button
              onClick={() => setTab("UDHAARI")}
              className={`pb-2 flex items-center gap-2 border-b-2 ${
                tab === "UDHAARI"
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500"
              }`}
            >
              <Users size={16} />
              Udhaari
            </button>

            <button
              onClick={() => setTab("INVOICES")}
              className={`pb-2 flex items-center gap-2 border-b-2 ${
                tab === "INVOICES"
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500"
              }`}
            >
              <Landmark size={16} />
              Invoices
            </button>
          </div>
        </div>

        {/* ================= UDHAARI ================= */}
        {tab === "UDHAARI" && (
          <>
            {/* HEADER OUTSIDE */}
            <div className="flex justify-between items-center mb-4 montserrat-font-1">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Customer Udhaari
                </h2>
              </div>

              <div className="flex gap-3">
                <div className="flex items-center border rounded-lg px-3 py-2 bg-white w-72">
                  <Search size={14} className="text-gray-400 mr-2" />
                  <input
                    className="outline-none text-sm w-full"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <select
                  value={creditFilter}
                  onChange={(e) => setCreditFilter(e.target.value)}
                  className="border rounded-lg px-3 py-2 bg-white text-sm outline-none min-w-[140px]"
                >
                  <option value="Y">Credit ON</option>
                  <option value="N">Credit OFF</option>
                  <option value="ALL">All</option>
                </select>

                <button
                  onClick={() => {
                    setEditing(null);
                    setForm({
                      customerName: "",
                      customerMobile: "",
                      creditLimit: "",
                      creditAllowed: "Y",
                    });
                    setDrawerOpen(true);
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex gap-2 items-center"
                >
                  <Plus size={16} />
                  Add Customer
                </button>

                <button
                  onClick={fetchCustomers}
                  className="border px-3 py-2 rounded-lg text-white text-sm bg-green-500 flex gap-2 items-center"
                >
                  <RotateCcw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-xl border shadow-sm overflow-auto h-full montserrat-font-1">
              <table className="w-full text-sm montserrat-font-1">
                <thead className="bg-green-100 sticky top-0 z-10 text-gray-500 text-xs uppercase">
                  <tr className="text-start">
                    <th className="p-4 text-left">ID</th>
                    <th className="p-4 text-left">Mobile</th>
                    <th className="p-4 text-left">Name</th>
                    <th className="p-4 text-left">Loyalty Points</th>
                    <th className="p-4 text-left">Credit Limit</th>
                    {/* <th className="p-4 text-left">Due Amount</th> */}
                    <th className="p-4 text-left">Created On</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="py-10">
                        <div className="flex justify-center">
                          <LoadingScreen text="Loading..." />
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-6 text-gray-400"
                      >
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r) => (
                      <tr
                        key={r.pk_customer_id}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="p-4 text-gray-700">
                          {r.pk_customer_id}
                        </td>

                        <td className="text-gray-600">
                          {String(r.customer_mobile)}
                        </td>

                        <td className="font-medium text-gray-800">
                          {r.customer_name}
                        </td>

                        <td className="text-left font-mono">
                          ₹{formatAmount(r.loyalty_points)}
                        </td>

                        {/* FIXED DATA */}
                        <td className="text-left font-mono">
                          ₹{formatAmount(r.creditLimit || r.credit)}
                        </td>

                        {/* <td className="text-left font-mono">
                        ₹{formatAmount(r.due_amount)}
                      </td> */}

                        <td className="text-left font-mono">
  {formatDateTime(r.created_on, true)}
</td>

                        {/* STATUS WITH ICON */}
                        <td>
                          {r.credit_allowed === "Y" ? (
                            <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-green-100 text-green-700 w-fit">
                              <CheckCircle2 size={14} />
                              Credit ON
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-red-100 text-red-600 w-fit">
                              <CircleSlash2 size={14} />
                              Credit OFF
                            </span>
                          )}
                        </td>

                        {/* ACTIONS */}
                        <td className="text-left pr-6">
                          <div className="flex justify-start gap-2">
                            <button
                              onClick={() => handleToggleCredit(r)}
                              className={` relative group p-2 rounded-lg border ${
                                r.credit_allowed === "Y"
                                  ? "bg-green-50 text-green-600 hover:bg-green-100"
                                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                              }`}
                              title="Toggle Credit"
                            >
                              {r.credit_allowed === "Y" ? (
                                <CheckCircle2 size={16} />
                              ) : (
                                <CircleSlash2 size={16} />
                              )}
                              <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-sm bg-white border text-gray-700 rounded shadow opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                                Credit ON/OFF
                              </span>
                            </button>

                            <button
                              onClick={() => {
                                setEditing(r);
                                setForm({
                                  customerName: r.customer_name,
                                  customerMobile: r.customer_mobile,
                                  loyaltyPoints: r.loyalty_points || 0,
                                  creditLimit: r.creditLimit || r.credit,
                                  creditAllowed: r.credit_allowed,
                                });
                                setDrawerOpen(true);
                              }}
                              className=" relative group p-2 rounded-lg border hover:bg-blue-50 text-blue-600"
                            >
                              <Pencil size={16} />
                              <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-sm bg-white border text-gray-700 rounded shadow opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                                Edit
                              </span>
                            </button>

                            <button
                              onClick={() => {
                                setDeleteId(r.pk_customer_id);
                                setDeleteOpen(true);
                              }}
                              className="relative group p-2 rounded-lg border hover:bg-red-50 text-red-600"
                            >
                              <Trash2 size={16} />

                              <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-sm bg-white border text-gray-700 rounded shadow opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                                Delete
                              </span>
                            </button>

                            <button
                              onClick={() => {
                                setBillRow(r);
                                setBillOpen(true);

                                setBillingName(r.customer_name || "");
                                setBillingGst(r.billing_gst || "");
                                setBillRemarks("");

                                setFromDate("");
                                setToDate("");

                                setBillType("auto");
                                setBillAmount("");
                                setBillUrl("");
                              }}
                              className=" relative group px-3 py-1 rounded-lg border bg-gray-50"
                            >
                              <FileText size={16} />
                              <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-sm bg-white border text-gray-700 rounded shadow opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                                Bill
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <UdhariActionModal
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          deleteOpen={deleteOpen}
          setDeleteOpen={setDeleteOpen}
          setDeleteId={setDeleteId}
          form={form}
          setForm={setForm}
          handleSubmit={handleSubmit}
          confirmDelete={confirmDelete}
          editing={editing}
        />
        <CreditBillModal
          billOpen={billOpen}
          closeBill={() => setBillOpen(false)}
          billRow={billRow}
          billingName={billingName}
          setBillingName={setBillingName}
          billingGst={billingGst}
          setBillingGst={setBillingGst}
          billRemarks={billRemarks}
          setBillRemarks={setBillRemarks}
          isOldBill={isOldBill}
          setIsOldBill={setIsOldBill}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          billType={billType}
          setBillType={setBillType}
          billAmount={billAmount}
          setBillAmount={setBillAmount}
          billLoading={billLoading}
          billUrl={billUrl}
          onFetchBill={onFetchBill}
          onSaveInvoice={onSaveInvoice}
          onSendToCustomer={onSendToCustomer}
          setBillUrl={setBillUrl}
        />

        <CustomerInvoiceImageModal
          open={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          customer={selectedCustomer}
        />

        {tab === "INVOICES" && (
          <InvoiceTable
            rows={invoiceRows}
            loading={invoiceLoading}
            onRefresh={fetchInvoices}
            onDeleteClick={handleDeleteInvoice}
            onPaymentClick={(row, data) => {
              handlePaymentSubmit(row, data);
            }}
          />
        )}
      </div>
    </>
  );
};

export default Udhari;
